import { useCallback, useMemo } from 'react';
import { EventVenueMap } from 'src/components/Events/VenueMap/EventVenueMap';
import { useContent } from 'src/contexts/ContentContext';
import { useEventMapContext } from 'src/contexts/EventMapContext';
import { useEventMetrics } from 'src/contexts/EventMetricsContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Stack } from 'src/core/ui';
import { useAutoCompMapSectionHandler } from 'src/hooks/useAutoCompMapSectionHandler';
import {
  HeatMapColors,
  HeatMapColorSteps,
  useHeatMapColor,
} from 'src/hooks/useHeatMapColor';
import { ContentId } from 'src/utils/constants/contentId';
import {
  EventSectionClearingPrices,
  SectionInfo,
  SectionScoreOverride,
} from 'src/WebApiController';

import { HeaderWidgetContainer } from '../../HeaderWidgetContainer';
import { EventSectionClearingPriceSectionDisplay } from './EventSectionClearingPriceSectionDisplay';
import { EventSectionClearingPricesTable } from './EventSectionClearingPricesTable';

const reversedHeatMapColorsForALeftToRightLegend = [...HeatMapColors].reverse();

export const EventSectionClearingPricesWidgetsContainer = () => {
  const { isMapLoading } = useEventMapContext();

  const sectionClearingPricesText = useContent(ContentId.SectionClearingPrices);

  const {
    selectedSectionIds,
    onSectionClicked,
    onToggleMirrors,
    onSelectedSectionsChange,
  } = useAutoCompMapSectionHandler(() => {});

  const { sectionClearingPrices } = useEventMetrics();

  const [sectionClearingPricesBySectionId, sectionScoreOverrides] =
    useMemo(() => {
      const sectionClearingPricesBySectionId: Record<
        number,
        EventSectionClearingPrices
      > = {};
      const sectionScoreOverrides: SectionScoreOverride[] = [];
      for (const s of sectionClearingPrices.sectionClearingPrices ?? []) {
        if (s.sectionId) {
          sectionClearingPricesBySectionId[s.sectionId] = s;
        }

        sectionScoreOverrides.push({
          sectionId: s.sectionId,
          rowId: 0,
          score: s.normalizedScore,
        } as SectionScoreOverride);
      }
      return [sectionClearingPricesBySectionId, sectionScoreOverrides];
    }, [sectionClearingPrices.sectionClearingPrices]);

  const onSectionHovered = useCallback(
    (hoveredSection: SectionInfo) => (
      <EventSectionClearingPriceSectionDisplay
        section={hoveredSection}
        sectionClearingPricesBySectionId={sectionClearingPricesBySectionId}
      />
    ),
    [sectionClearingPricesBySectionId]
  );

  const { getColor } = useHeatMapColor(sectionScoreOverrides);

  const filteredEventSectionClearingPrices = useMemo(
    () =>
      selectedSectionIds?.length > 0
        ? sectionClearingPrices?.sectionClearingPrices?.filter(
            (s) => s.sectionId && selectedSectionIds.includes(s.sectionId)
          )
        : sectionClearingPrices?.sectionClearingPrices,
    [sectionClearingPrices, selectedSectionIds]
  );

  return (
    <>
      <HeaderWidgetContainer title={sectionClearingPricesText} />
      <Stack direction="responsive" gap="xl" width="full">
        {sectionClearingPrices.isLoading || isMapLoading ? (
          <PosSpinner />
        ) : (
          <>
            <div style={{ flex: '1 1 60%' }}>
              <EventSectionClearingPricesTable
                sectionClearingPrices={
                  filteredEventSectionClearingPrices ?? undefined
                }
              />
            </div>
            <div style={{ minWidth: '40vh', minHeight: '40vh' }}>
              <EventVenueMap
                getColor={getColor}
                onSectionHovered={onSectionHovered}
                selectedSectionIds={Array.from(selectedSectionIds)}
                markedSectionIds={Array.from(selectedSectionIds)}
                onSectionClicked={onSectionClicked}
                onToggleMirrors={onToggleMirrors}
                setSelectedSections={onSelectedSectionsChange}
                colorBandProps={{
                  numOfSteps: HeatMapColorSteps,
                  spectrum: reversedHeatMapColorsForALeftToRightLegend,
                  labels: [ContentId.LessSales, ContentId.MoreSales],
                }}
              />
            </div>
          </>
        )}
      </Stack>
    </>
  );
};
